import { createApi } from "@reduxjs/toolkit/query/react";
import endpoints, { baseURL } from "data/api/v1/endpoints.ts";
import { baseQueryWithRedirect } from "features/api/utils/apiUtils.ts";
import { GetPermissionsResponse, GetPermissionsResponseData } from "types/api/permissions/permissions.ts";
import { AdminSearchResponse, AdminSearchResponseData } from "types/api/permissions/adminSearch.ts";
import { GetPeoplePermissionsResponse, GetPeoplePermissionsResponseData } from "types/api/permissions/people.ts";
import {
	UpdatePersonPermissionsRequest,
	UpdatePersonPermissionsResponse,
	UpdatePersonPermissionsResponseData
} from "types/api/permissions/updatePerson.ts";
import {
	UpdateEmailRecipientRequest,
	UpdateEmailRecipientResponseData
} from "types/api/permissions/updateEmailRecipients.ts";

export const permissionsApiSlice = createApi({
	reducerPath: "permissionsApi",
	baseQuery: baseQueryWithRedirect(baseURL),
	tagTypes: ["UserPermissions"],
	endpoints: (builder) => ({
		getPermissions: builder.query<GetPermissionsResponseData, void>({
			query: () => endpoints.permissions.getUserPermissions(),
			transformResponse: (response: GetPermissionsResponse) => response?.data
		}),
		getPermissionsById: builder.query<GetPermissionsResponseData, string | null>({
			query: (userId?: string | null) => endpoints.permissions.getUserPermissionsById(userId),
			transformResponse: (response: GetPermissionsResponse) => response?.data
		}),
		getAdminSearchResults: builder.query<AdminSearchResponseData, string | null>({
			query: (searchTerm: string) => endpoints.permissions.getAdminSearchResults(searchTerm),
			transformResponse: (response: AdminSearchResponse) => response?.data
		}),
		getUsers: builder.query<GetPeoplePermissionsResponseData, void>({
			query: () => endpoints.permissions.getUsers(),
			transformResponse: (response: GetPeoplePermissionsResponse) => response?.data,
			providesTags: ["UserPermissions"]
		}),
		updateUserPermissions: builder.mutation<UpdatePersonPermissionsResponseData, UpdatePersonPermissionsRequest>({
			query: (person: UpdatePersonPermissionsRequest) => ({
				url: endpoints.permissions.updateUserPermissions(person.userId ?? ""),
				method: "PUT",
				dataType: "json",
				headers: {
					"Content-type": "application/json; charset=UTF-8"
				},
				body: person
			}),
			transformResponse: (response: UpdatePersonPermissionsResponse) => response?.data,
			invalidatesTags: ["UserPermissions"]
		}),
		updateBillToEmailRecipients: builder.mutation<UpdateEmailRecipientResponseData, UpdateEmailRecipientRequest>({
			query: (request: UpdateEmailRecipientRequest) => ({
				url: endpoints.permissions.updateBillToEmailRecipients(),
				method: "PUT",
				dataType: "json",
				headers: {
					"Content-type": "application/json; charset=UTF-8"
				},
				body: request
			})
		})
	})
});

export const {
	useGetPermissionsQuery,
	useGetPermissionsByIdQuery,
	useLazyGetAdminSearchResultsQuery,
	useGetUsersQuery,
	useLazyGetUsersQuery,
	useUpdateUserPermissionsMutation,
	useUpdateBillToEmailRecipientsMutation
} = permissionsApiSlice;
